import React, { useEffect, useState, useRef } from 'react';
import {
    Card,
    Form,
    Select,
    Checkbox,
    Table,
    Breadcrumb,
    Space,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import debounce from 'lodash.debounce';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useSelector } from 'react-redux';

const { Option } = Select;

const breadcrumbItems = [
    { title: 'Setting' },
    { title: 'Permission' },
];

interface ModuleRight {
    module_id: number;
    module_url: string;
    module_name: string;
    module_parent: number;
    right_view: 'Y' | 'N';
    right_add: 'Y' | 'N';
    right_edit: 'Y' | 'N';
    right_delete: 'Y' | 'N';
    right_import?: 'Y' | 'N';
    right_export?: 'Y' | 'N';
    right_search?: 'Y' | 'N';
}

interface User {
    user_id: number;
    user_name: string;
}

interface Organization {
    organization_id: number;
    organization_name: string;
}

interface TableRow {
    key: number;
    module_name: string;
    module_url: string;
    module_parent: number;
    children?: TableRow[];
}

const actionMap: Record<string, keyof ModuleRight> = {
    view: 'right_view',
    create: 'right_add',
    edit: 'right_edit',
    delete: 'right_delete',
    import: 'right_import',
    export: 'right_export',
    search: 'right_search',
};

const allActions = Object.keys(actionMap);

const Permission = () => {
    const user = useSelector((state: any) => state.user?.user);
    const [form] = Form.useForm();

    const [selectedUser, setSelectedUser] = useState<number | null>(null);
    const [permissions, setPermissions] = useState<Record<number, string[]>>({});
    const [users, setUsers] = useState<User[]>([]);
    const [modules, setModules] = useState<ModuleRight[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);

    /* ------------------ BUILD MODULE TREE (ANY DEPTH) ------------------ */
    const buildModuleTree = (
        data: ModuleRight[],
        parentId = 0
    ): TableRow[] =>
        data
            .filter(item => item.module_parent === parentId)
            .map(item => {
                const children = buildModuleTree(data, item.module_id);

                return {
                    key: item.module_id,
                    module_name: item.module_name,
                    module_url: item.module_url,
                    module_parent: item.module_parent,
                    ...(children.length > 0 ? { children } : {}), // 🔥 critical
                };
            });


    const tableData: TableRow[] = buildModuleTree(modules);

    /* ------------------ FETCH USERS ------------------ */
    const fetchUsers = async (organizationId: number) => {
        try {
            const data = await apiRequest<User[]>({
                method: 'get',
                url: `/users?organization_id=${organizationId}`,
            });
            setUsers(data);
        } catch {
            showError('Failed to load users');
        }
    };

    /* ------------------ LOAD MODULES + PERMISSIONS ------------------ */
    useEffect(() => {
        if (!selectedUser) return;

        const loadPermissions = async () => {
            try {
                const modulesRes = await apiRequest<ModuleRight[]>({
                    method: 'get',
                    url: `modules?user_id=${selectedUser}`,
                });

                setModules(modulesRes);

                const permissionMap: Record<number, string[]> = {};
                modulesRes.forEach(mod => {
                    permissionMap[mod.module_id] = Object.entries(actionMap)
                        .filter(([_, key]) => mod[key] === 'Y')
                        .map(([action]) => action);
                });

                setPermissions(permissionMap);
            } catch {
                showError('Failed to load user permissions');
            }
        };

        loadPermissions();
    }, [selectedUser]);


    /* ------------------ UPDATE PERMISSION (DEBOUNCED) ------------------ */
    const debouncedUpdate = useRef(
        debounce(async payload => {
            try {
                await apiRequest({
                    method: 'put',
                    url: 'update-user-permission',
                    data: { permissions: [payload] },
                });
                showSuccess('Permission updated');
            } catch {
                showError('Failed to update permission');
            }
        }, 500)
    ).current;

    useEffect(() => {
        return () => debouncedUpdate.cancel();
    }, []);

    /* ------------------ HANDLE CHECKBOX CHANGE ------------------ */
    const handlePermissionChange = (
        moduleId: number,
        updatedValues: string[],
        clicked: string
    ) => {
        const actionsRequiringView = [
            'edit',
            'delete',
            'create',
            'import',
            'export',
            'search',
        ];

        let newPermissions = [...updatedValues];

        if (clicked === 'view' && !updatedValues.includes('view')) {
            newPermissions = [];
        } else if (
            actionsRequiringView.includes(clicked) &&
            !updatedValues.includes('view')
        ) {
            newPermissions.push('view');
        }

        setPermissions(prev => ({
            ...prev,
            [moduleId]: newPermissions,
        }));

        const payload: any = {
            user_id: selectedUser,
            module_id: moduleId,
        };

        Object.entries(actionMap).forEach(([action, key]) => {
            payload[key] = newPermissions.includes(action) ? 'Y' : 'N';
        });

        debouncedUpdate(payload);
    };

    /* ------------------ TABLE COLUMNS ------------------ */
    const columns = [
        {
            title: 'Module Name',
            dataIndex: 'module_name',
            key: 'module_name',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: TableRow) => {
                const selected = permissions[record.key] || [];

                return (
                    <>
                        {allActions.map(action => (
                            <Checkbox
                                key={action}
                                checked={selected.includes(action)}
                                onChange={e =>
                                    handlePermissionChange(
                                        record.key,
                                        e.target.checked
                                            ? [...selected, action]
                                            : selected.filter(a => a !== action),
                                        action
                                    )
                                }
                            >
                                {action}
                            </Checkbox>
                        ))}
                    </>
                );
            },
        },
    ];

    /* ------------------ LOAD ORGANIZATIONS ------------------ */
    useEffect(() => {
        apiRequest<Organization[]>({ method: 'get', url: '/organization' })
            .then(setOrganizations)
            .catch(() => showError('Failed to load organizations'));
    }, []);

    useEffect(() => {
        if (user?.organization_id && user?.user_role !== 'super-master') {
            fetchUsers(user.organization_id);
        }
    }, [user?.organization_id]);

    /* ------------------ RENDER ------------------ */
    return (
        <>
            <Space style={{ marginBottom: 16 }}>
                <Breadcrumb items={breadcrumbItems} />
            </Space>

            <Card title="User-Based Permission Management">
                <Form form={form} layout="inline">
                    <Space wrap>
                        {user?.user_role === 'super-master' && (
                            <Form.Item
                                name="organization_id"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select organization',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Select Organization"
                                    style={{ width: 250 }}
                                    options={organizations.map(org => ({
                                        label: org.organization_name,
                                        value: org.organization_id,
                                    }))}
                                    onChange={fetchUsers}
                                />
                            </Form.Item>
                        )}

                        <Form.Item required>
                            <Select
                                placeholder="Choose a user"
                                style={{ width: 250 }}
                                allowClear
                                showSearch
                                onChange={setSelectedUser}
                            >
                                {users.map(u => (
                                    <Option key={u.user_id} value={u.user_id}>
                                        {u.user_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Space>
                </Form>

                {selectedUser && modules.length > 0 && (
                    <Table
                        style={{ marginTop: 24 }}
                        dataSource={tableData}
                        columns={columns}
                        pagination={false}
                        expandable={{
                            defaultExpandAllRows: false,
                            rowExpandable: record => !!record.children,
                        }}
                    />
                )}
            </Card>
        </>
    );
};

export default Permission;
